'use strict';

const BaseScene = require('./base-scene');
const Button = require('../ui/button');
const Player = require('../entities/player');
const Boss = require('../entities/boss');
const PlayerShotPool = require('../gameplay/player-shot-pool');
const BossProjectilePool = require('../gameplay/boss-projectile-pool');
const BossPatternEmitter = require('../gameplay/boss-patterns');
const { getBossProjectileProfile } = require('../config/boss-projectiles');
const { circlesOverlap } = require('../gameplay/collision');
const { getPlayerScale } = require('../config/levels');
const { COLORS, GAME_WIDTH, GAME_HEIGHT } = require('../config/constants');
const { drawPanel, drawText, drawGlowText } = require('../ui/draw');

const STATES = Object.freeze({
  TRANSFORM: 'TRANSFORM',
  INTRO: 'INTRO',
  ACTIVE: 'ACTIVE',
  DEFEATED: 'DEFEATED',
  FAILED: 'FAILED'
});

class BossScene extends BaseScene {
  enter(payload) {
    this.runContext = payload.runContext || { ranked: false, runId: null, level: 1 };
    this.level = Math.max(1, Math.min(99, Number(this.runContext.level) || 1));
    this.config = payload.config || this.app.gameConfig.getLevelConfig(this.level);
    this.bossConfig = this.config.boss || this.app.gameConfig.getLevelConfig(this.level).boss;
    this.projectileProfile = getBossProjectileProfile(this.level);
    this.app.bossAssets.preloadAnimal(this.level);
    this.survivalDurationMs = Math.max(0, Number(payload.survivalDurationMs) || 0);
    this.pauseCount = Math.max(0, Number(payload.pauseCount) || 0);
    this.pauseDurationMs = Math.max(0, Number(payload.pauseDurationMs) || 0);
    this.pauseStartedAt = null;
    this.paused = false;
    this.forceResumePrompt = false;
    this.finished = false;
    this.dragging = false;
    this.coarsePointer = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    this.bounds = {
      left: 0,
      right: GAME_WIDTH,
      top: Math.max(142, this.app.renderer.safeTop + 106),
      bottom: Math.min(GAME_HEIGHT, this.app.renderer.safeBottom)
    };
    this.player = new Player(GAME_WIDTH / 2, this.bounds.bottom - 145, {
      planeColor: this.app.cosmeticColor('PLANE', COLORS.cyan),
      trailColor: this.app.cosmeticColor('TRAIL', COLORS.cyan),
      level: this.level,
      scale: getPlayerScale(this.level)
    });
    this.player.setMode('fighter', this.app.bossAssets.get('fighter'));
    this.boss = new Boss(this.bossConfig, this.app.bossAssets, this.bounds);
    this.playerShots = new PlayerShotPool(80);
    this.projectiles = new BossProjectilePool(240, this.projectileProfile);
    this.projectiles.setLimit(this.bossConfig.maxCarrots);
    this.emitter = new BossPatternEmitter(this.bossConfig, this.bounds, this.projectileProfile);
    this.totalBossActiveSeconds = 0;
    this.bossRetryCount = 0;
    this.state = STATES.TRANSFORM;
    this.stateTime = 0;
    this.remaining = Number(this.bossConfig.timeLimitSeconds) || 60;
    this.playerLives = 3;
    this.invulnerableRemaining = 0;
    this.shotTimer = 0;
    this.alternateCannon = false;
    this.pauseButton = new Button({
      x: 632,
      y: Math.max(36, this.app.renderer.safeTop + 10),
      width: 82,
      height: 68,
      label: 'Ⅱ',
      variant: 'secondary',
      fontSize: 29,
      onTap: () => this.setPaused(true)
    });
    this.continueButton = new Button({ x: 205, y: 710, width: 340, height: 92, label: '继续战斗', onTap: () => this.setPaused(false) });
    this.retryButton = new Button({ x: 145, y: 770, width: 460, height: 96, label: '重新挑战 Boss', onTap: () => this.retryBoss() });
    this.exitButton = new Button({ x: 145, y: 900, width: 460, height: 88, label: '返回选关', variant: 'secondary', onTap: () => this.exitToLevels() });
    this.app.feedback.transform();
  }

  exit() {
    this.dragging = false;
    this.app.input.cancelPointer();
    if (!this.finished) this.app.abandonRun(this.runContext);
    this.playerShots.clear();
    this.projectiles.clear();
    super.exit();
  }

  setPaused(paused) {
    if (![STATES.TRANSFORM, STATES.INTRO, STATES.ACTIVE].includes(this.state)) return;
    const next = Boolean(paused);
    if (next === this.paused) return;
    this.paused = next;
    if (next) {
      this.pauseCount += 1;
      this.pauseStartedAt = performance.now();
      this.dragging = false;
      this.app.input.cancelPointer();
    } else {
      if (this.pauseStartedAt !== null) this.pauseDurationMs += Math.max(0, performance.now() - this.pauseStartedAt);
      this.pauseStartedAt = null;
      this.forceResumePrompt = false;
    }
    this.app.feedback.setMusicPaused(next);
  }

  onAppHide() {
    if (!this.finished && [STATES.TRANSFORM, STATES.INTRO, STATES.ACTIVE].includes(this.state)) {
      this.setPaused(true);
      this.forceResumePrompt = true;
    }
  }

  onAppShow() {
    if (this.forceResumePrompt) this.paused = true;
  }

  changeState(next) {
    if (this.state === next) return false;
    this.state = next;
    this.stateTime = 0;
    return true;
  }

  resetAttempt() {
    this.playerLives = 3;
    this.invulnerableRemaining = 0;
    this.remaining = Number(this.bossConfig.timeLimitSeconds) || 60;
    this.shotTimer = 0;
    this.alternateCannon = false;
    this.player.x = GAME_WIDTH / 2;
    this.player.y = this.bounds.bottom - 145;
    this.player.targetX = this.player.x;
    this.player.targetY = this.player.y;
    this.boss.reset(this.bossConfig);
    this.playerShots.clear();
    this.projectiles.clear();
    this.emitter = new BossPatternEmitter(this.bossConfig, this.bounds, this.projectileProfile);
  }

  retryBoss() {
    if (this.state !== STATES.FAILED) return;
    this.resetAttempt();
    this.app.feedback.setMusicPaused(false);
    this.changeState(STATES.INTRO);
  }

  exitToLevels() {
    if (this.finished) return;
    this.finished = true;
    this.app.abandonRun(this.runContext);
    this.app.performSwitch('levelSelect', { page: Math.floor((this.level - 1) / 15) });
  }

  failAttempt() {
    if (this.state !== STATES.ACTIVE) return;
    this.bossRetryCount = Math.min(100, this.bossRetryCount + 1);
    this.dragging = false;
    this.app.input.cancelPointer();
    this.app.feedback.setMusicPaused(true);
    this.changeState(STATES.FAILED);
  }

  completeVictory() {
    if (this.finished) return;
    this.finished = true;
    const settlementPromise = this.app.completeRun(this.runContext, {
      cleared: true,
      activeDurationMs: this.survivalDurationMs,
      pauseCount: this.pauseCount,
      pauseDurationMs: Math.round(this.pauseDurationMs),
      bossDurationMs: Math.round(this.totalBossActiveSeconds * 1000),
      bossRetryCount: this.bossRetryCount,
      bossDefeated: true
    });
    this.app.performSwitch('result', {
      success: true,
      level: this.level,
      elapsed: this.survivalDurationMs / 1000,
      ranked: this.runContext.ranked,
      settlementPromise
    });
  }

  update(dt) {
    if (this.paused || this.finished || this.state === STATES.FAILED) return;
    this.stateTime += dt;
    if (this.state === STATES.TRANSFORM) {
      this.player.update(dt, this.bounds);
      if (this.stateTime >= 1.5) this.changeState(STATES.INTRO);
      return;
    }
    if (this.state === STATES.INTRO) {
      this.boss.update(dt);
      if (this.stateTime >= 0.8) this.changeState(STATES.ACTIVE);
      return;
    }
    if (this.state === STATES.DEFEATED) {
      if (this.stateTime >= 1.2) this.completeVictory();
      return;
    }
    if (this.state !== STATES.ACTIVE) return;
    this.updateActive(dt);
  }

  updateActive(dt) {
    this.totalBossActiveSeconds += dt;
    this.remaining = Math.max(0, this.remaining - dt);
    this.invulnerableRemaining = Math.max(0, this.invulnerableRemaining - dt);
    this.player.update(dt, this.bounds);
    this.boss.update(dt);
    this.shotTimer -= dt;
    if (this.shotTimer <= 0) {
      const cannon = this.player.getCannonPositions(this.alternateCannon);
      this.alternateCannon = !this.alternateCannon;
      this.playerShots.spawn({ x: cannon.x, y: cannon.y, speed: this.bossConfig.playerShotSpeed, damage: 1 });
      this.shotTimer += Math.max(0.05, Number(this.bossConfig.playerShotInterval) || 0.16);
      this.app.feedback.laser();
    }
    if (this.emitter.update(dt, this.boss, this.player, this.projectiles) > 0) this.app.feedback.carrot();
    this.playerShots.update(dt, this.bounds);
    this.projectiles.update(dt, this.bounds, this.player);
    if (this.resolveShotHits()) return;
    this.resolvePlayerHits();
    if (this.state === STATES.ACTIVE && this.remaining <= 0) this.failAttempt();
  }

  resolveShotHits() {
    const bossCircle = this.boss.getHitCircle();
    const shots = this.playerShots.getActive();
    for (let index = 0; index < shots.length; index += 1) {
      if (!circlesOverlap(shots[index].getHitCircle(), bossCircle)) continue;
      shots[index].active = false;
      const defeated = this.boss.takeDamage(shots[index].damage);
      this.app.feedback.bossDamage();
      if (defeated) {
        this.playerShots.clear();
        this.projectiles.clear();
        this.app.feedback.setMusicPaused(true);
        this.changeState(STATES.DEFEATED);
        this.app.feedback.bossDefeated();
        return true;
      }
    }
    return false;
  }

  resolvePlayerHits() {
    if (this.invulnerableRemaining > 0) return;
    const playerCircle = this.player.getHitCircle();
    const projectiles = this.projectiles.getActive();
    let hit = circlesOverlap(playerCircle, this.boss.getHitCircle());
    for (let index = 0; !hit && index < projectiles.length; index += 1) {
      if (circlesOverlap(playerCircle, projectiles[index].getHitCircle())) {
        projectiles[index].active = false;
        hit = true;
      }
    }
    if (!hit) return;
    this.playerLives -= 1;
    this.invulnerableRemaining = 1.2;
    this.app.feedback.playerDamage();
    if (this.playerLives <= 0) this.failAttempt();
  }

  handleTouch(type, point, gesture) {
    if (this.finished) return;
    if (this.paused) {
      if (type === 'start' && this.continueButton.contains(point.x, point.y)) {
        this.app.feedback.tap(); this.continueButton.tap();
      }
      return;
    }
    if (this.state === STATES.FAILED) {
      if (type === 'start' && this.retryButton.contains(point.x, point.y)) {
        this.app.feedback.tap(); this.retryButton.tap();
      } else if (type === 'start' && this.exitButton.contains(point.x, point.y)) {
        this.app.feedback.tap(); this.exitButton.tap();
      }
      return;
    }
    if (type === 'start' && this.pauseButton.contains(point.x, point.y)) {
      this.app.feedback.tap(); this.pauseButton.tap(); return;
    }
    if (this.coarsePointer) {
      if (type === 'move' && gesture && (gesture.dx || gesture.dy)) {
        const delta = this.app.renderer.clientDeltaToLogical(gesture.dx, gesture.dy);
        const sensitivity = this.app.gameConfig.getTouchSensitivity(this.app.settings.touchSensitivity);
        this.player.moveBy(delta.x * sensitivity, delta.y * sensitivity, this.bounds);
      }
      return;
    }
    if (type === 'start' && this.player.contains(point.x, point.y)) {
      this.dragging = true; this.player.setTarget(point.x, point.y);
    } else if (type === 'move' && this.dragging) {
      this.player.setTarget(point.x, point.y);
    } else if (type === 'end' || type === 'cancel') {
      this.dragging = false;
    }
  }

  handleKeyboard(dt, vector) {
    if (this.finished || this.paused || this.state === STATES.FAILED || (!vector.x && !vector.y)) return;
    this.player.moveBy(vector.x * 340 * dt, vector.y * 340 * dt, this.bounds);
  }

  render(ctx) {
    this.player.fighterImage = this.app.bossAssets.get('fighter') || this.player.fighterImage;
    this.drawArena(ctx);
    if (this.state !== STATES.TRANSFORM || this.stateTime > 0.65) this.boss.draw(ctx);
    this.playerShots.draw(ctx);
    this.projectiles.draw(ctx);
    if (this.invulnerableRemaining <= 0 || Math.floor(this.invulnerableRemaining * 12) % 2 === 0) this.player.draw(ctx);
    this.drawHud(ctx);
    this.pauseButton.draw(ctx);
    if (this.state === STATES.TRANSFORM) this.drawTransform(ctx);
    if (this.state === STATES.INTRO) this.drawIntro(ctx);
    if (this.state === STATES.DEFEATED) this.drawDefeated(ctx);
    if (this.state === STATES.FAILED) this.drawFailed(ctx);
    if (this.paused) this.drawPause(ctx);
  }

  drawArena(ctx) {
    ctx.save();
    const gradient = ctx.createLinearGradient(0, this.bounds.top, 0, this.bounds.bottom);
    gradient.addColorStop(0, 'rgba(120,25,105,.18)');
    gradient.addColorStop(0.48, 'rgba(18,55,122,.12)');
    gradient.addColorStop(1, 'rgba(2,8,25,.3)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, this.bounds.top, GAME_WIDTH, this.bounds.bottom - this.bounds.top);
    ctx.strokeStyle = 'rgba(255,92,220,.24)'; ctx.lineWidth = 2;
    ctx.strokeRect(10, this.bounds.top + 8, GAME_WIDTH - 20, this.bounds.bottom - this.bounds.top - 16);
    ctx.restore();
  }

  drawHud(ctx) {
    const top = Math.max(30, this.app.renderer.safeTop + 6);
    drawPanel(ctx, 24, top, 592, 86, { radius: 20, fill: 'rgba(5,17,43,.88)', stroke: 'rgba(255,92,220,.45)' });
    drawText(ctx, `${this.bossConfig.animalName} Boss`, 92, top + 27, 20, '#ff8be7', 'center', 'bold');
    const healthRatio = Math.max(0, this.boss.health / this.boss.maxHealth);
    ctx.fillStyle = 'rgba(255,255,255,.14)'; ctx.fillRect(166, top + 17, 314, 18);
    ctx.fillStyle = healthRatio > 0.3 ? '#ff5d8f' : '#ffb238'; ctx.fillRect(166, top + 17, 314 * healthRatio, 18);
    drawText(ctx, `${Math.ceil(this.boss.health)} / ${this.boss.maxHealth}`, 323, top + 26, 17, '#fff');
    drawText(ctx, `生命 ${'◆'.repeat(Math.max(0, this.playerLives))}${'◇'.repeat(Math.max(0, 3 - this.playerLives))}`, 210, top + 62, 21, COLORS.cyan);
    drawText(ctx, `${this.remaining.toFixed(1)}s`, 502, top + 62, 24, COLORS.warning, 'center', 'bold');
  }

  drawTransform(ctx) {
    ctx.save(); ctx.globalAlpha = Math.min(1, this.stateTime * 1.4);
    ctx.strokeStyle = COLORS.cyan; ctx.lineWidth = 5; ctx.shadowColor = COLORS.cyan; ctx.shadowBlur = 28;
    ctx.beginPath(); ctx.arc(this.player.x, this.player.y, 70 + this.stateTime * 42, 0, Math.PI * 2); ctx.stroke();
    drawGlowText(ctx, '战斗形态启动', GAME_WIDTH / 2, 560, 48, COLORS.cyan);
    ctx.restore();
  }

  drawIntro(ctx) {
    drawGlowText(ctx, `${this.bossConfig.animalName} Boss · ${this.level}`, GAME_WIDTH / 2, 520, 43, '#ff8be7');
    drawText(ctx, `${this.projectileProfile.projectileName} · ${this.bossConfig.patterns[0]}`, GAME_WIDTH / 2, 572, 22, COLORS.warning);
  }

  drawDefeated(ctx) {
    ctx.save(); ctx.fillStyle = `rgba(255,255,255,${Math.max(0, 0.35 - this.stateTime * 0.22)})`; ctx.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT); ctx.restore();
    drawGlowText(ctx, 'BOSS 击破', GAME_WIDTH / 2, 600, 62, COLORS.success);
  }

  drawFailed(ctx) {
    ctx.save(); ctx.fillStyle = 'rgba(1,5,16,.78)'; ctx.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT); ctx.restore();
    drawGlowText(ctx, 'Boss 挑战失败', GAME_WIDTH / 2, 545, 58, COLORS.danger);
    drawText(ctx, '躲避阶段已保留，可直接重试 Boss', GAME_WIDTH / 2, 620, 25, COLORS.muted);
    this.retryButton.draw(ctx); this.exitButton.draw(ctx);
  }

  drawPause(ctx) {
    ctx.save(); ctx.fillStyle = 'rgba(1,5,16,.8)'; ctx.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT); ctx.restore();
    drawGlowText(ctx, '战斗暂停', GAME_WIDTH / 2, 590, 56, COLORS.cyan);
    drawText(ctx, '准备好后继续', GAME_WIDTH / 2, 655, 27, COLORS.muted);
    this.continueButton.draw(ctx);
  }
}

BossScene.STATES = STATES;
module.exports = BossScene;
